import axios from 'axios';
import { authStore } from '$lib/stores/auth';

// Set default timeout for all requests (30 seconds)
axios.defaults.timeout = 120000;

// Enable credentials (cookies) for all requests
axios.defaults.withCredentials = true;

/**
 * Guards against a burst of 401s firing a session check each. Only the first
 * one asks; the rest ride on its answer.
 */
let sessionCheckInFlight: Promise<void> | null = null;

/**
 * Logs out only if the session is *actually* gone.
 *
 * A single 401 is not proof: a request landing while the server restarts, or a
 * momentary blip, will return one while the cookie is still perfectly good. The
 * old code logged out on the first 401 it saw, and because logout clears the
 * cookie server-side that was unrecoverable — one unlucky request during a deploy
 * signed every live client out for good.
 *
 * `/api/auth/session` is in the allowlist below, so its own 401 cannot recurse
 * back into here.
 */
function logoutIfSessionIsGone(): Promise<void> {
	if (sessionCheckInFlight) return sessionCheckInFlight;

	sessionCheckInFlight = axios
		.get('/api/auth/session', { withCredentials: true })
		.then(() => {
			// Still authenticated — the 401 was transient, so keep the session.
		})
		.catch(() => {
			authStore.logout();
		})
		.finally(() => {
			sessionCheckInFlight = null;
		});

	return sessionCheckInFlight;
}

// Add response interceptor to handle 401 errors
axios.interceptors.response.use(
	(response) => response,
	(error) => {
		// Only logout for 401 if NOT on public routes or auth check endpoints
		const url = error.config?.url || '';
		const isPublicRoute = url.includes('/public/') ||
							  url.includes('/api/auth/agent-login') ||
							  url.includes('/api/auth/room-login') ||
							  url.includes('/api/auth/session') ||
							  url.includes('/api/auth/phone-login') ||
							  url.includes('/api/auth/send-verification') ||
							  url.includes('/api/auth/register');

		if (error.response?.status === 401 && !isPublicRoute) {
			// Confirm before tearing the session down; see above.
			void logoutIfSessionIsGone();
		}
		return Promise.reject(error);
	}
);

export default axios;
